import type { ProUpgradeView } from '../hooks/use-pro-paywall-page'
import {
  PRO_UPGRADE_CREDIT_LABEL,
  PRO_UPGRADE_DUE_TODAY_LABEL,
  PRO_UPGRADE_PLAN_LABEL,
} from '../select-pro-upgrade-charge'

export type ProUpgradePanelProps = {
  wuPriceLabel: string
  upgrade: ProUpgradeView
  onReview: () => void
}

function ChargeRow({
  label,
  value,
  emphasis,
}: {
  label: string
  value: string
  emphasis?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span
        className={
          emphasis ? 'text-sm font-semibold text-foreground' : 'text-sm text-muted-foreground'
        }
      >
        {label}
      </span>
      <span
        className={emphasis ? 'text-sm font-semibold text-foreground' : 'text-sm text-foreground'}
      >
        {value}
      </span>
    </div>
  )
}

export function ProUpgradePanel({ wuPriceLabel, upgrade, onReview }: ProUpgradePanelProps) {
  const renewalLine =
    upgrade.renewalDateLabel === ''
      ? `Then ${upgrade.renewalAmountLabel}.`
      : `Then ${upgrade.renewalAmountLabel} on ${upgrade.renewalDateLabel}.`

  return (
    <div className="flex flex-col gap-4" data-testid="pro-upgrade-panel">
      <div className="rounded-xl border-2 border-primary bg-primary/10 p-4">
        <p className="text-base font-semibold text-foreground">Add Waterfowl U</p>
        <p className="mt-1 text-sm font-semibold text-primary">
          {wuPriceLabel === '' ? 'Includes BlindBook Pro' : `${wuPriceLabel} · Includes BlindBook Pro`}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Your Pro subscription becomes the Waterfowl U bundle right away. Your current
          BlindBook Pro plan price is credited against the bundle price.
        </p>
      </div>

      {upgrade.blockedReason ? (
        <p className="text-sm text-muted-foreground">{upgrade.blockedReason}</p>
      ) : upgrade.isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="pro-upgrade-loading">
          Pricing your upgrade…
        </p>
      ) : !upgrade.canConfirm ? null : (
        <div
          className="flex flex-col gap-2 rounded-lg bg-muted p-4"
          data-testid="pro-upgrade-summary"
        >
          {upgrade.isTrial ? null : (
            <>
              {upgrade.planPriceLabel === '' ? null : (
                <ChargeRow label={PRO_UPGRADE_PLAN_LABEL} value={upgrade.planPriceLabel} />
              )}
              {upgrade.creditLabel === '' ? null : (
                <ChargeRow label={PRO_UPGRADE_CREDIT_LABEL} value={upgrade.creditLabel} />
              )}
            </>
          )}
          <div className="border-t pt-2">
            <ChargeRow
              label={PRO_UPGRADE_DUE_TODAY_LABEL}
              value={upgrade.isTrial ? 'Nothing to pay today' : upgrade.amountDueNowLabel}
              emphasis
            />
          </div>
          {upgrade.renewalAmountLabel === '' ? null : (
            <p className="text-sm text-muted-foreground">{renewalLine}</p>
          )}
          {upgrade.cardLabel === '' ? null : (
            <p className="text-sm text-muted-foreground">{upgrade.cardLabel}</p>
          )}
          {upgrade.periodResetLine === '' ? null : (
            <p className="text-sm text-muted-foreground">{upgrade.periodResetLine}</p>
          )}
        </div>
      )}

      {upgrade.error ? (
        <p role="alert" className="text-sm text-destructive">
          {upgrade.error}
        </p>
      ) : null}

      {upgrade.blockedReason ? null : (
        <button
          type="button"
          data-testid="pro-review-upgrade"
          disabled={!upgrade.canConfirm || upgrade.isConfirming}
          onClick={onReview}
          className="w-full rounded-lg bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {upgrade.isConfirming ? 'Upgrading…' : 'Add Waterfowl U'}
        </button>
      )}
    </div>
  )
}
